'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import LinkIcon from '@/components/icons/link-icon';
import { TelegramIcon } from '@/components/icons/telegram';
import { VkIcon } from '@/components/icons/vk';
import { showSuccessToast, showWarningToast } from '@/components/ui/sonner';
import { getShareLinks } from '@/utils/social-share';
import type { SelectionBubbleState, SelectionShareAction } from '../types/lexical.types';

const INITIAL_BUBBLE: SelectionBubbleState = {
  visible: false,
  x: 0,
  y: 0,
  arrowOffset: 0,
  paragraphId: '',
  paragraphText: '',
  selectionText: '',
};

function getParagraphElement(node: Node | null): HTMLElement | null {
  if (!node) return null;
  const element = node instanceof Element ? node : (node.parentElement as HTMLElement | null);
  return element?.closest('[data-paragraph-id]') ?? null;
}

export function useSelectionShareBubble(containerRef: React.RefObject<HTMLDivElement | null>) {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [selectionBubble, setSelectionBubble] = useState<SelectionBubbleState>(INITIAL_BUBBLE);

  const hideSelectionBubble = useCallback(
    () => setSelectionBubble((prev) => (prev.visible ? { ...prev, visible: false } : prev)),
    [],
  );

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0 || !containerRef.current) {
      hideSelectionBubble();
      return;
    }

    const range = selection.getRangeAt(0);
    const commonNode = range.commonAncestorContainer;
    if (!containerRef.current.contains(commonNode)) {
      hideSelectionBubble();
      return;
    }

    const paragraphElement =
      getParagraphElement(selection.anchorNode) ||
      getParagraphElement(selection.focusNode) ||
      getParagraphElement(commonNode);

    const rects = range.getClientRects();
    const rect = rects.length > 0 ? rects[rects.length - 1] : range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      hideSelectionBubble();
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const anchorX = rect.right - containerRect.left;
    const bubbleWidth = 126;
    const clampedX = Math.min(
      Math.max(anchorX - bubbleWidth, 0),
      (containerRef.current.clientWidth || 0) - bubbleWidth,
    );
    const arrowOffset = Math.min(Math.max(anchorX - clampedX, 12), bubbleWidth - 12);
    const top = rect.bottom - containerRect.top + 12;

    setSelectionBubble({
      visible: true,
      x: clampedX,
      y: top,
      arrowOffset,
      paragraphId: paragraphElement?.dataset.paragraphId || '',
      paragraphText: paragraphElement?.textContent?.trim() || '',
      selectionText: selection.toString().trim(),
    });
  }, [containerRef, hideSelectionBubble]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (
        bubbleRef.current &&
        event.target instanceof Node &&
        bubbleRef.current.contains(event.target)
      ) {
        return;
      }
      hideSelectionBubble();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [handleSelectionChange, hideSelectionBubble]);

  useEffect(() => {
    if (!selectionBubble.visible) return;

    const handleScroll = () => {
      const currentSelection = window.getSelection();
      if (!currentSelection || currentSelection.isCollapsed) {
        hideSelectionBubble();
        return;
      }
      handleSelectionChange();
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleSelectionChange, hideSelectionBubble, selectionBubble.visible]);

  const getShareUrlForSelection = useCallback(() => {
    if (typeof window === 'undefined') return '';

    const url = new URL(window.location.href);
    if (selectionBubble.paragraphId) {
      url.hash = selectionBubble.paragraphId;
    }
    return url.toString();
  }, [selectionBubble.paragraphId]);

  const handleCopyLink = useCallback(async () => {
    const url = getShareUrlForSelection();
    if (!url) return;

    try {
      const copyViaClipboardApi = async () => {
        if (typeof navigator === 'undefined') return false;
        if (!navigator.clipboard?.writeText) return false;
        await navigator.clipboard.writeText(url);
        return true;
      };

      const copyViaExecCommand = () => {
        if (typeof document === 'undefined') return false;
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);

        textarea.focus();
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);

        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      };

      const copied = (await copyViaClipboardApi()) || copyViaExecCommand();
      if (!copied) {
        throw new Error('copy_failed');
      }

      showSuccessToast('Ссылка скопирована в буфер обмена');
    } catch (error) {
      console.error('Failed to copy link:', error);
      showWarningToast('Не удалось скопировать ссылку');
    } finally {
      hideSelectionBubble();
      window.getSelection()?.removeAllRanges();
    }
  }, [getShareUrlForSelection, hideSelectionBubble]);

  const handleShare = useCallback(
    (platform: 'telegram' | 'vk') => {
      if (typeof window === 'undefined') return;

      const url = getShareUrlForSelection();
      if (!url) return;

      const title =
        selectionBubble.selectionText?.trim() ||
        selectionBubble.paragraphText?.trim() ||
        document.title ||
        '';

      const shareLinks = getShareLinks(url, title);
      const shareUrl = shareLinks[platform];

      const windowFeatures =
        platform === 'vk' ? 'width=600,height=400,noopener,noreferrer' : 'noopener,noreferrer';

      window.open(shareUrl, '_blank', windowFeatures);
      hideSelectionBubble();
      window.getSelection()?.removeAllRanges();
    },
    [
      getShareUrlForSelection,
      hideSelectionBubble,
      selectionBubble.paragraphText,
      selectionBubble.selectionText,
    ],
  );

  const selectionActions: SelectionShareAction[] = [
    {
      id: 'copy',
      color: '#A84DFF',
      Icon: LinkIcon,
      title: 'Копировать ссылку',
      onClick: handleCopyLink,
    },
    {
      id: 'telegram',
      color: '#FF3DAE',
      Icon: TelegramIcon,
      title: 'Поделиться в Telegram',
      onClick: () => handleShare('telegram'),
    },
    {
      id: 'vk',
      color: '#505CFF',
      Icon: VkIcon,
      title: 'Поделиться в VK',
      onClick: () => handleShare('vk'),
    },
  ];

  return {
    bubbleRef,
    selectionBubble,
    selectionActions,
  };
}
